import { rehydrateStatementFromSharedDefaults } from "@/dashboard/inventory_management/common/cancelAiMapping";
import { useI18n } from "vue-i18n";

/**
 * Represents a single cancellation statement from AI analysis
 */
interface AiAnalysisStatement {
  beneficiary_name?: string;
  registry_code?: string;
  mwh_cancelled?: number;
  beneficiary_type?: string;
  residency?: string;
  address?: string;
  period_start?: string;
  period_end?: string;
  cancellation_purpose?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Structure of the form data returned from AI analysis
 * Extends AiAnalysisStatement to inherit all statement properties
 * while adding form-specific properties
 */
interface AiAnalysisFormData extends AiAnalysisStatement {
  statements?: AiAnalysisStatement[];
  shared_defaults?: {
    beneficiary_type?: string;
    period_start?: string;
    period_end?: string;
    cancellation_purpose?: string;
  };
  global_operation_notes?: string;
}

/**
 * Complete AI analysis response structure
 */
export interface AiAnalysisResponse {
  form_data: AiAnalysisFormData;
  [key: string]: any; // Allow additional response properties
}

/**
 * Extracted and validated analysis data
 */
export interface ExtractedAnalysisData {
  formData: AiAnalysisFormData;
  hasMultipleStatements: boolean;
  firstStatement: AiAnalysisStatement;
  totalStatements: number;
}

/**
 * Helper functions for AI analysis workflow
 */
export const useAiAnalysisHelpers = () => {
  const { t } = useI18n();

  /**
   * Generate success message based on analysis results
   */
  const generateSuccessMessage = (
    hasMultipleStatements: boolean,
    updatedFieldsCount: number,
    totalStatements: number
  ): string => {
    if (hasMultipleStatements) {
      return updatedFieldsCount > 0
        ? t(
            "cancellation.modal.analysis_success_multiple_statements_with_fields",
            {
              count: updatedFieldsCount,
              total_statements: totalStatements,
            }
          )
        : t(
            "cancellation.modal.analysis_success_multiple_statements_no_fields",
            {
              total_statements: totalStatements,
            }
          );
    } else {
      return updatedFieldsCount > 0
        ? t("cancellation.modal.analysis_success_with_fields", {
            count: updatedFieldsCount,
          })
        : t("cancellation.modal.analysis_success_no_fields");
    }
  };

  /**
   * Show multiple statements warning with delay
   */
  const showMultipleStatementsWarning = (
    totalStatements: number,
    globalErrorHandler: (error: any) => void,
    delay = 2000
  ) => {
    setTimeout(() => {
      globalErrorHandler({
        message: t("cancellation.modal.multiple_statements_warning", {
          total_statements: totalStatements,
        }),
        type: "warning",
      });
    }, delay);
  };

  /**
   * Extract and validate analysis data structure
   *
   * @throws {Error} When data structure is invalid
   */
  const extractAnalysisData = (
    data: AiAnalysisResponse
  ): ExtractedAnalysisData | null => {
    if (!data || !data.form_data) {
      return null;
    }

    const formData = data.form_data;
    const hasMultipleStatements = Boolean(
      formData.statements && formData.statements.length > 1
    );
    const rawFirstStatement = formData.statements?.[0] || formData;
    // A statement may omit exactly the fields this modal prefills, so rehydrate it from
    // shared_defaults first.
    const firstStatement: AiAnalysisStatement =
      rehydrateStatementFromSharedDefaults(
        rawFirstStatement,
        formData.shared_defaults
      );

    return {
      formData,
      hasMultipleStatements,
      firstStatement,
      totalStatements: formData.statements?.length || 1,
    };
  };

  return {
    generateSuccessMessage,
    showMultipleStatementsWarning,
    extractAnalysisData,
  };
};
